import React from 'react';
import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import { DailyReportModel } from '../../../../../../models/audit/DailyReportModel';
import PdfCommonHeader from '../shared';

interface PosOnlyPdfDocumentProps {
  storeName: string;
  address: string;
  reportDate: string;
  dailyReportResponse: DailyReportModel;
}

type TileStatus = 'Deficit' | 'Matched' | 'Surplus';

const CHECK_ICON_URL = 'https://frontend-dev.sofmen.com/Email-template/double-checkmark.png';
const CHECK_SMALL_ICON_URL = 'https://frontend-dev.sofmen.com/Email-template/check.png';
const GAME_COL_WIDTHS = {
  name: 216,
  rate: 76,
  pack: 80,
  range: 52,
  auditQty: 44,
  auditAmount: 61,
  posQty: 52,
  posAmount: 61,
  variance: 70,
  varianceAmount: 104,
};

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    padding: 0,
    paddingTop: 0,
    fontFamily: 'Helvetica',
  },
  tilesWrap: {
    flexDirection: 'row',
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 6,
    paddingBottom: 6,
  },
  tileCell: {
    width: '25%',
    padding: 6,
  },
  spacerCell: {
    width: '25%',
    padding: 6,
  },
  tile: {
    width: '100%',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#A3AED0',
    backgroundColor: '#FAFAFC',
  },
  tileHeader: {
    minHeight: 24,
    backgroundColor: '#ECEEF4',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    paddingLeft: 4,
    paddingRight: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tileHeaderCentered: {
    minHeight: 24,
    backgroundColor: '#ECEEF4',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    paddingLeft: 4,
    paddingRight: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#222B45',
  },
  badge: {
    borderRadius: 4,
    paddingTop: 3,
    paddingBottom: 3,
    paddingLeft: 6,
    paddingRight: 6,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: 700,
    color: '#ffffff',
    textTransform: 'uppercase',
  },
  tileBody: {
    padding: 4,
    minHeight: 62,
  },
  tileCenter: {
    minHeight: 32,
    marginTop: 4,
    marginBottom: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileMainTextBlue: {
    fontSize: 13,
    fontWeight: 700,
    color: '#0000FF',
  },
  tileMainTextRed: {
    fontSize: 13,
    fontWeight: 700,
    color: '#dc2626',
  },
  tileSubText: {
    fontSize: 12,
    color: '#6b7280',
    fontStyle: 'italic',
    fontWeight: 600,
    textAlign: 'center',
  },
  tileFooter: {
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#A3AED0',
    fontSize: 12,
    color: '#111827',
    fontWeight: 700,
    textAlign: 'center',
  },
  checkIcon: {
    width: 36,
    height: 18,
    objectFit: 'contain',
  },
  shiftSection: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 0,
    paddingBottom: 0,
  },
  shiftTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  shiftTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#222B45',
    marginRight: 8,
  },
  shiftTable: {
    width: '100%',
  },
  shiftHeadRow: {
    backgroundColor: '#F0F2F7',
    flexDirection: 'row',
  },
  shiftHeadCell: {
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#222B45',
    borderBottomWidth: 1,
    borderBottomColor: '#A3AED0',
    fontWeight: 700,
  },
  shiftHeadCellSub: {
    color: '#6b7280',
    fontStyle: 'italic',
    fontSize: 11,
    fontWeight: 400,
  },
  colTime: { width: '28%' },
  colUser: { width: '18%' },
  colSales: { width: '18%' },
  colInventory: { width: '18%' },
  colVariance: { width: '18%' },
  shiftBodyRow: {
    flexDirection: 'row',
  },
  shiftCell: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#111827',
  },
  shiftCellRight: {
    textAlign: 'right',
  },
  shiftMutedInline: {
    color: '#6b7280',
    fontStyle: 'italic',
  },
  shiftVarianceBlue: {
    color: '#0000FF',
  },
  shiftVarianceRed: {
    color: '#FF0000',
  },
  shiftVarianceMatched: {
    color: '#2C6CA0',
    textAlign: 'right',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  shiftCheckIcon: {
    width: 12,
    height: 12,
    objectFit: 'contain',
  },
  shiftFootRow: {
    flexDirection: 'row',
  },
  shiftFootCell: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#111827',
    fontWeight: 700,
    borderTopWidth: 1,
    borderTopColor: '#A3AED0',
    borderBottomWidth: 1,
    borderBottomColor: '#A3AED0',
  },
  gameSection: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 12,
  },
  gameTitle: {
    marginBottom: 4,
    fontSize: 14,
    fontWeight: 700,
    color: '#222B45',
  },
  gameTable: {
    width: '100%',
  },
  gameHeadRow: {
    backgroundColor: '#F0F2F7',
    flexDirection: 'row',
  },
  gameHeadCell: {
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#222B45',
    borderBottomWidth: 1,
    borderBottomColor: '#A3AED0',
    fontWeight: 700,
  },
  gColName: { width: GAME_COL_WIDTHS.name },
  gColRate: { width: GAME_COL_WIDTHS.rate },
  gColPack: { width: GAME_COL_WIDTHS.pack },
  gColRange: { width: GAME_COL_WIDTHS.range },
  gColAuditQty: { width: GAME_COL_WIDTHS.auditQty },
  gColAuditAmount: { width: GAME_COL_WIDTHS.auditAmount },
  gColPosQty: { width: GAME_COL_WIDTHS.posQty },
  gColPosAmount: { width: GAME_COL_WIDTHS.posAmount },
  gColVariance: { width: GAME_COL_WIDTHS.variance },
  gColVarianceAmount: { width: GAME_COL_WIDTHS.varianceAmount },
  gameRow: {
    flexDirection: 'row',
  },
  gameSharedCell: {
    justifyContent: 'center',
  },
  gameStackCell: {
    justifyContent: 'flex-start',
  },
  gameStackLine: {
    minHeight: 18,
    justifyContent: 'center',
  },
  gameStackLineDivider: {
    borderBottomWidth: 0,
  },
  gameCell: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#111827',
  },
  gameCellBold: {
    fontWeight: 600,
  },
  gameFootRow: {
    flexDirection: 'row',
  },
  gameFootCell: {
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 12,
    color: '#222B45',
    borderTopWidth: 1,
    borderTopColor: '#A3AED0',
    borderBottomWidth: 1,
    borderBottomColor: '#A3AED0',
    fontWeight: 700,
  },
  rateCellGap: {
    paddingRight: 10,
  },
  packCellGap: {
    paddingLeft: 10,
  },
  posLotteryRow: {
    paddingTop: 4,
    paddingBottom: 0,
    paddingLeft: 12,
    paddingRight: 12,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  infoLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: '#2C6CA0',
  },
  infoValue: {
    fontSize: 12,
    fontWeight: 400,
    color: '#111827',
  },
  infoSpacer: {
    width: 16,
  },
});

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const formatSignedCurrency = (value: number) => {
  if (value > 0) return `+${formatCurrency(value)}`;
  if (value < 0) return `-${formatCurrency(Math.abs(value))}`;
  return formatCurrency(0);
};

const formatSignedNumber = (value: number) => {
  if (value > 0) return `+${value}`;
  return `${value}`;
};

const formatPosDataWindow = (fromRaw?: string, toRaw?: string) => {
  const from = moment(fromRaw);
  const to = moment(toRaw);
  if (!from.isValid() || !to.isValid()) return '-';
  return `${from.format('h:mm A')} - ${to.format('h:mm A')}`;
};

const formatThreeDigitRange = (start: number, end: number) => {
  const startText = Number(start || 0).toString().padStart(3, '0');
  const endText = Number(end || 0).toString().padStart(3, '0');
  return `${startText}-${endText}`;
};

const getStatusColor = (status: TileStatus) => {
  if (status === 'Matched') return '#038423';
  if (status === 'Surplus') return '#0000FF';
  return '#FF0000';
};

const StatusBadge: React.FC<{ status: TileStatus }> = ({ status }) => (
  <View style={[styles.badge, { backgroundColor: getStatusColor(status) }]}>
    <Text style={styles.badgeText}>{status}</Text>
  </View>
);

const resolveVarianceStatus = (revenueVariance: number, qtyVariance: number): TileStatus => {
  if (revenueVariance === 0 && qtyVariance === 0) return 'Matched';
  const signSource = revenueVariance !== 0 ? revenueVariance : qtyVariance;
  return signSource > 0 ? 'Surplus' : 'Deficit';
};

const PosOnlyPdfDocument: React.FC<PosOnlyPdfDocumentProps> = ({
  storeName,
  address,
  reportDate,
  dailyReportResponse,
}) => {
  const summaryTotals = (dailyReportResponse as any)?.summaryTotals;
  const serverDailyTotals = (dailyReportResponse as any)?._serverData?.daily_totals;
  const posTotals = serverDailyTotals?.pos ?? summaryTotals?.pos ?? {};
  const posDataFrom = dailyReportResponse.posDataReceivedAtFrom || (dailyReportResponse as any)?._serverData?.pos_data_received_at?.from;
  const posDataTo = dailyReportResponse.posDataReceivedAtTo || (dailyReportResponse as any)?._serverData?.pos_data_received_at?.to;
  const posDataWindowText = formatPosDataWindow(posDataFrom, posDataTo);

  const lottoSales = Number(posTotals?.lotto_sales ?? 0);
  const posPayout = Number(posTotals?.payout ?? 0);
  const audits = Array.isArray((dailyReportResponse as any)?.audits) ? (dailyReportResponse as any).audits : [];
  const games = (((dailyReportResponse as any)?._serverData?.games || []) as any[]);

  const scratcherSales = Number(dailyReportResponse.scratcherSales || 0);
  const scratcherCount = Number(dailyReportResponse.scratcherCount || 0);
  const scratcherRevenueVariance = Number(dailyReportResponse.scratcherSalesVariance || 0);
  const scratcherQtyVariance = Number(dailyReportResponse.scratcherCountVariance || 0);
  const scratcherStatus = resolveVarianceStatus(scratcherRevenueVariance, scratcherQtyVariance);

  return (
    <Document title="Daily Reconciliation Summary">
      <Page size={[816, 1056]} style={styles.page}>
        <PdfCommonHeader storeName={storeName} address={address} reportDate={reportDate} />

        <View style={styles.tilesWrap}>
          <View style={styles.tileCell}>
            <View style={styles.tile}>
              <View style={styles.tileHeader}>
                <Text style={styles.tileTitle}>SCRATCHER</Text>
                <StatusBadge status={scratcherStatus} />
              </View>
              <View style={styles.tileBody}>
                <View style={styles.tileCenter}>
                  {scratcherStatus === 'Matched' ? (
                    <Image src={CHECK_ICON_URL} style={styles.checkIcon} />
                  ) : scratcherStatus === 'Surplus' ? (
                    <Text style={styles.tileMainTextBlue}>{`+${formatCurrency(scratcherRevenueVariance)}`}</Text>
                  ) : (
                    <Text style={styles.tileMainTextRed}>{formatCurrency(scratcherRevenueVariance)}</Text>
                  )}

                  {scratcherStatus === 'Deficit' && (
                    <Text style={styles.tileSubText}>
                      {Math.abs(scratcherQtyVariance)} {Math.abs(scratcherQtyVariance) === 1 ? 'ticket missing' : 'tickets missing'}
                    </Text>
                  )}
                </View>
                <Text style={styles.tileFooter}>
                  Total: {formatCurrency(scratcherSales)} ({scratcherCount})
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.spacerCell} />

          <View style={styles.tileCell}>
            <View style={styles.tile}>
              <View style={styles.tileHeaderCentered}>
                <Text style={styles.tileTitle}>LOTTO</Text>
              </View>
              <View style={styles.tileBody}>
                <View style={styles.tileCenter}>
                  <Text style={styles.tileMainTextBlue}>{formatCurrency(lottoSales)}</Text>
                </View>
                <Text style={styles.tileFooter}>Reported by POS</Text>
              </View>
            </View>
          </View>

          <View style={styles.tileCell}>
            <View style={styles.tile}>
              <View style={styles.tileHeaderCentered}>
                <Text style={styles.tileTitle}>PAYOUTS</Text>
              </View>
              <View style={styles.tileBody}>
                <View style={styles.tileCenter}>
                  <Text style={styles.tileMainTextBlue}>{formatCurrency(posPayout)}</Text>
                </View>
                <Text style={styles.tileFooter}>Reported by POS</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.shiftSection}>
          <View style={styles.shiftTitleRow}>
            <Text style={styles.shiftTitle}>Scratcher Audits</Text>
            <StatusBadge status={scratcherStatus} />
          </View>

          <View style={styles.shiftTable}>
            <View style={styles.shiftHeadRow}>
              <Text style={[styles.shiftHeadCell, styles.colTime]}>Time of Audit</Text>
              <Text style={[styles.shiftHeadCell, styles.colUser]}>User</Text>
              <Text style={[styles.shiftHeadCell, styles.colSales, { textAlign: 'right' }]}>
                Sales Reported{'\n'}
                <Text style={styles.shiftHeadCellSub}>By the POS</Text>
              </Text>
              <Text style={[styles.shiftHeadCell, styles.colInventory, { textAlign: 'right' }]}>
                Inventory Removed{'\n'}
                <Text style={styles.shiftHeadCellSub}>From display case</Text>
              </Text>
              <Text style={[styles.shiftHeadCell, styles.colVariance, { textAlign: 'right' }]}>Variance</Text>
            </View>

            {[...audits.filter((audit: any) => {
              const varianceRevenue = Number(audit.reconciliation_variance?.scratcher?.revenue_variance ?? 0);
              const varianceQty = Number(audit.reconciliation_variance?.scratcher?.quantity_variance ?? 0);
              return varianceRevenue !== 0 || varianceQty !== 0;
            }), ...audits.filter((audit: any) => {
              const varianceRevenue = Number(audit.reconciliation_variance?.scratcher?.revenue_variance ?? 0);
              const varianceQty = Number(audit.reconciliation_variance?.scratcher?.quantity_variance ?? 0);
              return varianceRevenue === 0 && varianceQty === 0;
            })].map((audit: any) => {
              const varianceRevenue = Number(audit.reconciliation_variance?.scratcher?.revenue_variance ?? 0);
              const varianceQty = Number(audit.reconciliation_variance?.scratcher?.quantity_variance ?? 0);
              const varianceIsMatched = varianceRevenue === 0 && varianceQty === 0;
              const varianceSignSource = varianceRevenue !== 0 ? varianceRevenue : varianceQty;
              const varianceColor = varianceSignSource > 0 ? styles.shiftVarianceBlue : styles.shiftVarianceRed;

              return (
                <View style={styles.shiftBodyRow} key={String(audit.audit_id)}>
                  <Text style={[styles.shiftCell, styles.colTime]}>
                    {moment(audit.audit_date).format('MM/DD/YYYY, hh:mm A')}
                  </Text>
                  <Text style={[styles.shiftCell, styles.colUser]}>
                    {audit.user_name || '-'}
                  </Text>
                  <Text style={[styles.shiftCell, styles.shiftCellRight, styles.colSales]}>
                    {formatCurrency(Number(audit.pos_summary?.scratcher_sales ?? 0))}{' '}
                    <Text style={styles.shiftMutedInline}>({Number(audit.pos_summary?.scratcher_count ?? 0)})</Text>
                  </Text>
                  <Text style={[styles.shiftCell, styles.shiftCellRight, styles.colInventory]}>
                    {formatCurrency(Number(audit.audit_summary?.total_revenue ?? 0))}{' '}
                    <Text style={styles.shiftMutedInline}>({Number(audit.audit_summary?.total_sold ?? 0)})</Text>
                  </Text>

                  {varianceIsMatched ? (
                    <View style={[styles.shiftCell, styles.colVariance, styles.shiftVarianceMatched]}>
                      <Image src={CHECK_SMALL_ICON_URL} style={styles.shiftCheckIcon} />
                    </View>
                  ) : (
                    <Text style={[styles.shiftCell, styles.shiftCellRight, styles.colVariance, varianceColor]}>
                      {formatSignedCurrency(varianceRevenue)}{' '}
                      <Text style={varianceColor}>({formatSignedNumber(varianceQty)})</Text>
                    </Text>
                  )}
                </View>
              );
            })}

            <View style={styles.shiftFootRow}>
              <Text style={[styles.shiftFootCell, { width: '46%' }]}>Total</Text>
              <Text style={[styles.shiftFootCell, styles.shiftCellRight, styles.colSales]}>
                {formatCurrency(Number(dailyReportResponse.scratcherSales || 0))}{' '}
                <Text style={styles.shiftMutedInline}>({Number(dailyReportResponse.scratcherCount || 0)})</Text>
              </Text>
              <Text style={[styles.shiftFootCell, styles.shiftCellRight, styles.colInventory]}>
                {formatCurrency(Number(dailyReportResponse.auditTotalRevenue || 0))}{' '}
                <Text style={styles.shiftMutedInline}>({Number(dailyReportResponse.auditTotalSold || 0)})</Text>
              </Text>
              {(() => {
                const totalRevenueVariance = Number(dailyReportResponse.scratcherSalesVariance || 0);
                const totalQtyVariance = Number(dailyReportResponse.scratcherCountVariance || 0);
                const totalVarianceMatched = totalRevenueVariance === 0 && totalQtyVariance === 0;
                const totalSignSource = totalRevenueVariance !== 0 ? totalRevenueVariance : totalQtyVariance;
                const totalVarianceColor = totalSignSource > 0 ? styles.shiftVarianceBlue : styles.shiftVarianceRed;

                return totalVarianceMatched ? (
                <View style={[styles.shiftFootCell, styles.colVariance, styles.shiftVarianceMatched]}>
                  <Image src={CHECK_SMALL_ICON_URL} style={styles.shiftCheckIcon} />
                </View>
              ) : (
                <Text
                  style={[
                    styles.shiftFootCell,
                    styles.shiftCellRight,
                    styles.colVariance,
                    totalVarianceColor
                  ]}
                >
                  {formatSignedCurrency(totalRevenueVariance)}{' '}
                  <Text
                    style={totalVarianceColor}
                  >
                    ({formatSignedNumber(totalQtyVariance)})
                  </Text>
                </Text>
              );
              })()}
            </View>
          </View>
        </View>

        <View style={styles.gameSection}>
          <Text style={styles.gameTitle}>All Games</Text>

          <View style={styles.gameTable}>
            <View style={styles.gameHeadRow} wrap={false}>
              <Text style={[styles.gameHeadCell, styles.gColName]}>Game Name</Text>
              <Text style={[styles.gameHeadCell, styles.gColRate, styles.rateCellGap, { textAlign: 'right' }]}>Rate</Text>
              <Text style={[styles.gameHeadCell, styles.gColPack, styles.packCellGap]}>Pack</Text>
              <Text style={[styles.gameHeadCell, styles.gColRange]}>Range</Text>
              <Text style={[styles.gameHeadCell, styles.gColAuditQty, { textAlign: 'right' }]}>Audit Qty</Text>
              <Text style={[styles.gameHeadCell, styles.gColAuditAmount, { textAlign: 'right' }]}>Audit $</Text>
              <Text style={[styles.gameHeadCell, styles.gColPosQty, { textAlign: 'right' }]}>POS Qty</Text>
              <Text style={[styles.gameHeadCell, styles.gColPosAmount, { textAlign: 'right' }]}>POS $</Text>
              <Text style={[styles.gameHeadCell, styles.gColVariance, { textAlign: 'right' }]}>Variance</Text>
              <Text style={[styles.gameHeadCell, styles.gColVarianceAmount, { textAlign: 'right' }]}>Variance</Text>
            </View>

            {(() => {
              const PACK_LINE_HEIGHT = 18;
              type GameBlock = {
                key: string;
                gameName: string;
                rate: number;
                packLines: Array<{ packId: string; range: string }>;
                auditQty: number;
                auditAmount: number;
                posQty: number;
                posAmount: number;
                varianceQty: number;
                varianceAmount: number;
                hasVariance: boolean;
              };

              const gameBlocks: GameBlock[] = games.map((game: any, gameIdx: number) => {
                const packs = Array.isArray(game?.packs) && game.packs.length > 0
                  ? game.packs
                  : [{ pack_id: '', starting_ticket_number: 0, ending_ticket_number: 0 }];

                const packLines = packs.map((pack: any, packIdx: number) => ({
                  packId: String(pack?.pack_id ?? ''),
                  range: formatThreeDigitRange(
                    Number(pack?.starting_ticket_number ?? 0),
                    Number(pack?.ending_ticket_number ?? 0)
                  ),
                  key: `${game?.game_id ?? gameIdx}-${pack?.pack_id ?? packIdx}-${packIdx}`,
                }));

                const varianceQty = Number(game?.reconciliation_variance?.quantity_variance ?? 0);
                const varianceAmount = Number(game?.reconciliation_variance?.revenue_variance ?? 0);

                return {
                  key: `${game?.game_id ?? gameIdx}`,
                  gameName: String(game?.game_name ?? ''),
                  rate: Number(game?.cost_per_ticket ?? 0),
                  packLines,
                  auditQty: Number(game?.audit_totals?.total_sold ?? 0),
                  auditAmount: Number(game?.audit_totals?.total_revenue ?? 0),
                  posQty: Number(game?.pos_totals?.scratcher_count ?? 0),
                  posAmount: Number(game?.pos_totals?.scratcher_sales ?? 0),
                  varianceQty,
                  varianceAmount,
                  hasVariance: varianceQty !== 0 || varianceAmount !== 0,
                };
              });

              const totals = gameBlocks.reduce((acc, game) => ({
                rate: acc.rate + game.rate,
                packs: acc.packs + game.packLines.length,
                auditQty: acc.auditQty + game.auditQty,
                auditAmount: acc.auditAmount + game.auditAmount,
                posQty: acc.posQty + game.posQty,
                posAmount: acc.posAmount + game.posAmount,
                varianceQty: acc.varianceQty + game.varianceQty,
                varianceAmount: acc.varianceAmount + game.varianceAmount,
              }), {
                rate: 0,
                packs: 0,
                auditQty: 0,
                auditAmount: 0,
                posQty: 0,
                posAmount: 0,
                varianceQty: 0,
                varianceAmount: 0,
              });

              return (
                <>
                  {[...gameBlocks.filter((row) => row.hasVariance), ...gameBlocks.filter((row) => !row.hasVariance)].map((row) => {
                    const blockHeight = Math.max(1, row.packLines.length) * PACK_LINE_HEIGHT;
                    return (
                      <View key={row.key} style={styles.gameRow} wrap={false}>
                        <View style={[styles.gColName, styles.gameSharedCell, { height: blockHeight }]}>
                          <Text style={[styles.gameCell, ...(row.hasVariance ? [styles.gameCellBold] : [])]}>{row.gameName}</Text>
                        </View>
                        <View style={[styles.gColRate, styles.gameSharedCell, { height: blockHeight }]}>
                          <Text style={[styles.gameCell, styles.rateCellGap, ...(row.hasVariance ? [styles.gameCellBold] : []), { textAlign: 'right' }]}>
                            {formatCurrency(row.rate)}
                          </Text>
                        </View>

                        <View style={[styles.gColPack, styles.gameStackCell, { height: blockHeight }]}>
                          {row.packLines.map((packLine, idx) => (
                            <View
                              key={`${row.key}-pack-${idx}`}
                              style={[styles.gameStackLine, idx < row.packLines.length - 1 ? styles.gameStackLineDivider : {}]}
                            >
                              <Text style={[styles.gameCell, styles.packCellGap, ...(row.hasVariance ? [styles.gameCellBold] : [])]}>
                                {packLine.packId}
                              </Text>
                            </View>
                          ))}
                        </View>

                        <View style={[styles.gColRange, styles.gameStackCell, { height: blockHeight }]}>
                          {row.packLines.map((packLine, idx) => (
                            <View
                              key={`${row.key}-range-${idx}`}
                              style={[styles.gameStackLine, idx < row.packLines.length - 1 ? styles.gameStackLineDivider : {}]}
                            >
                              <Text style={[styles.gameCell, ...(row.hasVariance ? [styles.gameCellBold] : [])]}>
                                {packLine.range}
                              </Text>
                            </View>
                          ))}
                        </View>

                        <View style={[styles.gColAuditQty, styles.gameSharedCell, { height: blockHeight, alignItems: 'flex-end' }]}>
                          <Text style={[styles.gameCell, ...(row.hasVariance ? [styles.gameCellBold] : []), { textAlign: 'right' }]}>{row.auditQty}</Text>
                        </View>
                        <View style={[styles.gColAuditAmount, styles.gameSharedCell, { height: blockHeight, alignItems: 'flex-end' }]}>
                          <Text style={[styles.gameCell, ...(row.hasVariance ? [styles.gameCellBold] : []), { textAlign: 'right' }]}>
                            {formatCurrency(row.auditAmount)}
                          </Text>
                        </View>
                        <View style={[styles.gColPosQty, styles.gameSharedCell, { height: blockHeight, alignItems: 'flex-end' }]}>
                          <Text style={[styles.gameCell, ...(row.hasVariance ? [styles.gameCellBold] : []), { textAlign: 'right' }]}>{row.posQty}</Text>
                        </View>
                        <View style={[styles.gColPosAmount, styles.gameSharedCell, { height: blockHeight, alignItems: 'flex-end' }]}>
                          <Text style={[styles.gameCell, ...(row.hasVariance ? [styles.gameCellBold] : []), { textAlign: 'right' }]}>
                            {formatCurrency(row.posAmount)}
                          </Text>
                        </View>

                        {row.varianceQty === 0 ? (
                          <View style={[styles.gColVariance, styles.gameSharedCell, { height: blockHeight, alignItems: 'flex-end' }]}>
                            <Image src={CHECK_SMALL_ICON_URL} style={styles.shiftCheckIcon} />
                          </View>
                        ) : (
                          <View style={[styles.gColVariance, styles.gameSharedCell, { height: blockHeight, alignItems: 'flex-end' }]}>
                            <Text style={[styles.gameCell, ...(row.hasVariance ? [styles.gameCellBold] : []), { textAlign: 'right' }]}>
                              {row.varianceQty}
                            </Text>
                          </View>
                        )}

                        {row.varianceAmount === 0 ? (
                          <View style={[styles.gColVarianceAmount, styles.gameSharedCell, { height: blockHeight, alignItems: 'flex-end' }]}>
                            <Image src={CHECK_SMALL_ICON_URL} style={styles.shiftCheckIcon} />
                          </View>
                        ) : (
                          <View style={[styles.gColVarianceAmount, styles.gameSharedCell, { height: blockHeight, alignItems: 'flex-end' }]}>
                            <Text style={[styles.gameCell, ...(row.hasVariance ? [styles.gameCellBold] : []), { textAlign: 'right' }]}>
                              {formatCurrency(row.varianceAmount)}
                            </Text>
                          </View>
                        )}
                      </View>
                    );
                  })}

                  <View style={styles.gameFootRow} wrap={false}>
                    <Text style={[styles.gameFootCell, styles.gColName]}>Total</Text>
                    <Text style={[styles.gameFootCell, styles.gColRate, styles.rateCellGap, { textAlign: 'right' }]}></Text>
                    <Text style={[styles.gameFootCell, styles.gColPack, styles.packCellGap]}>{totals.packs} packs</Text>
                    <Text style={[styles.gameFootCell, styles.gColRange]}></Text>
                    <Text style={[styles.gameFootCell, styles.gColAuditQty, { textAlign: 'right' }]}>{totals.auditQty}</Text>
                    <Text style={[styles.gameFootCell, styles.gColAuditAmount, { textAlign: 'right' }]}>{formatCurrency(totals.auditAmount)}</Text>
                    <Text style={[styles.gameFootCell, styles.gColPosQty, { textAlign: 'right' }]}>{totals.posQty}</Text>
                    <Text style={[styles.gameFootCell, styles.gColPosAmount, { textAlign: 'right' }]}>{formatCurrency(totals.posAmount)}</Text>
                    <Text style={[styles.gameFootCell, styles.gColVariance, { textAlign: 'right' }]}>{totals.varianceQty}</Text>
                    <Text style={[styles.gameFootCell, styles.gColVarianceAmount, { textAlign: 'right' }]}>{formatCurrency(totals.varianceAmount)}</Text>
                  </View>
                </>
              );
            })()}
          </View>
        </View>

        <View style={styles.posLotteryRow}>
          <Text style={styles.infoLabel}>
            Point of Sale Data:
            <Text style={styles.infoValue}> {posDataWindowText}</Text>
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default PosOnlyPdfDocument;
